'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  group?: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: 'ChartBarIcon', group: 'Operations' },
  { label: 'Users & Drivers', href: '/user-driver-management', icon: 'UsersIcon', group: 'Operations' },
  { label: 'Notifications', href: '/notification-feed', icon: 'BellAlertIcon', group: 'Operations' },
  { label: 'Complaints', href: '/dispute-resolution', icon: 'ScaleIcon', group: 'Operations' },
  { label: 'Discounts & Pricing', href: '/pricing-surge-management', icon: 'TicketIcon', group: 'Configuration' },
  { label: 'Payments & Settlements', href: '/payments-settlements', icon: 'CreditCardIcon', group: 'Configuration' },
  { label: 'Jobs', href: '/jobs', icon: 'TruckIcon', group: 'Operations' },
  { label: 'Payouts', href: '/payouts', icon: 'BanknotesIcon', group: 'Operations' },
  { label: 'Markets', href: '/markets', icon: 'GlobeAltIcon', group: 'Configuration' },
  { label: 'Document Review', href: '/document-review', icon: 'DocumentCheckIcon', group: 'Operations' },
  { label: 'Settings', href: '/admin-settings', icon: 'Cog6ToothIcon', group: 'Configuration' },
  { label: 'Reports & Analytics', href: '/reports-analytics', icon: 'DocumentChartBarIcon', group: 'Insights' },
];

const groups = ['Operations', 'Configuration', 'Insights'];

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  city_manager: 'City Manager',
  support_agent: 'Support Agent',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { profile, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const isActive = (href: string) => {
    if (href === '/dashboard' && pathname === '/') return true;
    return pathname === href || pathname.startsWith(href + '/');
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch {
      setSigningOut(false);
    }
  };

  const displayName = profile?.full_name || 'Admin User';
  const displayRole = profile?.role ? (roleLabels[profile.role] || profile.role) : 'Admin';
  const displayInitials = profile?.avatar_initials || displayName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <aside
      className={`
        flex flex-col bg-secondary text-secondary-foreground h-screen sticky top-0 z-40
        sidebar-transition overflow-hidden flex-shrink-0
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-white/10 flex-shrink-0 ${collapsed ? 'justify-center px-0' : 'px-4 gap-3'}`}>
        <div className="flex items-center gap-2">
          {/*
            The cube mark alone, not the full lockup.

            What was here was the complete logo -- cube, "LEJO" wordmark and
            rule -- rendered at 32 pixels, where the word is an illegible
            smudge. It also had a white background baked in, so it drew a
            white box on the #1A1A2E sidebar.

            This is the light variant: the same mark, with its charcoal
            recoloured for a dark ground. The original measures 1.73:1 against
            this sidebar -- effectively invisible, leaving only the gold to
            carry the shape. The light one measures 14.2:1. The gold is
            untouched in both.
          */}
          <Image
            src="/assets/images/logo-mark-light.png"
            alt="Lejo"
            width={30}
            height={30}
            className="flex-shrink-0 object-contain"
            priority
          />
          {!collapsed && (
            <span className="font-bold text-lg tracking-tight text-white">
              Lejo<span className="text-primary">Admin</span>
            </span>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          if (!items.length) return null;
          return (
            <div key={`group-${group}`} className="mb-2">
              {!collapsed && (
                <p className="px-4 py-1.5 text-[10px] font-600 tracking-widest uppercase text-white/30">
                  {group}
                </p>
              )}
              {items.map((item) => {
                const active = isActive(item.href);
                return (
                  <div key={`nav-${item.href}`} className="relative group/nav px-2 mb-0.5">
                    <Link
                      href={item.href}
                      className={`
                        flex items-center rounded-lg transition-all duration-150
                        ${collapsed ? 'justify-center h-10 w-10 mx-auto' : 'gap-3 px-3 py-2.5'}
                        ${active
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-white/60 hover:bg-white/10 hover:text-white'
                        }
                      `}
                    >
                      <Icon name={item.icon as any} size={18} variant="outline" className="flex-shrink-0" />
                      {!collapsed && (
                        <span className="text-sm font-500 truncate flex-1">{item.label}</span>
                      )}
                      {!collapsed && item.badge && item.badge > 0 && (
                        <span className="bg-primary/80 text-white text-[10px] font-700 rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge && item.badge > 0 && (
                        <span className="absolute top-0 right-1 bg-primary text-white text-[9px] font-700 rounded-full w-4 h-4 flex items-center justify-center leading-none">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                    {/* Tooltip on collapsed */}
                    {collapsed && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 pointer-events-none opacity-0 group-hover/nav:opacity-100 transition-opacity duration-150">
                        <div className="bg-foreground text-background text-xs font-500 px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-elevated">
                          {item.label}
                          {item.badge ? ` (${item.badge})` : ''}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-white/10 p-2 flex-shrink-0">
        <button
          onClick={onToggle}
          className={`
            w-full flex items-center rounded-lg text-white/50 hover:text-white hover:bg-white/10
            transition-all duration-150 py-2
            ${collapsed ? 'justify-center' : 'gap-3 px-3'}
          `}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
            size={16}
            variant="outline"
          />
          {!collapsed && <span className="text-xs font-500">Collapse</span>}
        </button>

        {!collapsed && (
          <div className="mt-1 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-3 py-2 hover:bg-white/10 transition-all duration-150">
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-700 flex-shrink-0">
                {displayInitials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 text-white truncate">{displayName}</p>
                <p className="text-[10px] text-white/40 truncate">{displayRole}</p>
              </div>
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="text-white/30 hover:text-white/80 transition-colors flex-shrink-0"
                aria-label="Sign out"
                title="Sign out"
              >
                {signingOut ? (
                  <Icon name="ArrowPathIcon" size={14} variant="outline" className="animate-spin" />
                ) : (
                  <Icon name="ArrowRightOnRectangleIcon" size={14} variant="outline" />
                )}
              </button>
            </div>
          </div>
        )}

        {collapsed && (
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center justify-center rounded-lg text-white/30 hover:text-white/80 hover:bg-white/10 transition-all duration-150 py-2 mt-1"
            aria-label="Sign out"
            title="Sign out"
          >
            {signingOut ? (
              <Icon name="ArrowPathIcon" size={14} variant="outline" className="animate-spin" />
            ) : (
              <Icon name="ArrowRightOnRectangleIcon" size={14} variant="outline" />
            )}
          </button>
        )}
      </div>
    </aside>
  );
}